import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Upload, X, Image as ImageIcon } from 'lucide-react';

interface SumoImageUploadProps {
  sumoId: string;
  onImagesUploaded: (imageUrls: string[]) => void;
  maxFiles?: number;
}

interface UploadProgress {
  id: string;
  file: File;
  progress: number;
  url?: string;
  error?: string;
}

export const SumoImageUpload = ({ sumoId, onImagesUploaded, maxFiles = 10 }: SumoImageUploadProps) => {
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const onDrop = async (acceptedFiles: File[]) => {
    if (!sumoId) {
      toast({
        title: "Erreur",
        description: "ID du sumo requis pour l'upload",
        variant: "destructive",
      });
      return;
    }

    if (acceptedFiles.length === 0) return;

    setIsUploading(true);
    const newUploads: UploadProgress[] = acceptedFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      progress: 0,
    }));

    setUploads(prev => [...prev, ...newUploads]);

    const uploadedUrls: string[] = [];

    for (const upload of newUploads) {
      try {
        const fileExt = upload.file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
        const filePath = `${sumoId}/${fileName}`;

        // Upload to Supabase storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('sumo-images')
          .upload(filePath, upload.file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('sumo-images')
          .getPublicUrl(filePath);

        // Save to database
        const { error: dbError } = await supabase
          .from('sumo_images')
          .insert({
            sumo_id: sumoId,
            image_url: publicUrl,
            alt_text: `Image du sumo ${sumoId}`,
            is_primary: false,
            display_order: 0
          });

        if (dbError) throw dbError;

        uploadedUrls.push(publicUrl);

        setUploads(prev => prev.map(u => 
          u.id === upload.id ? { ...u, progress: 100, url: publicUrl } : u
        ));

      } catch (error) {
        console.error('Erreur upload:', error);
        setUploads(prev => prev.map(u => 
          u.id === upload.id ? { ...u, error: error instanceof Error ? error.message : 'Erreur inconnue' } : u
        ));
      }
    }

    setIsUploading(false);
    onImagesUploaded(uploadedUrls);

    if (uploadedUrls.length > 0) {
      toast({
        title: "Succès",
        description: `${uploadedUrls.length} image(s) uploadée(s) avec succès`,
      });

      // Clear uploads after a delay
      setTimeout(() => {
        setUploads([]);
      }, 3000);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    maxFiles,
    disabled: isUploading
  });

  const removeUpload = (id: string) => {
    setUploads(prev => prev.filter(u => u.id !== id));
  };

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-primary bg-primary/5'
            : 'border-gray-300 hover:border-gray-400'
        } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input {...getInputProps()} />
        <ImageIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        {isDragActive ? (
          <p className="text-lg font-medium">Déposez les images ici...</p>
        ) : (
          <div>
            <p className="text-lg font-medium mb-2">
              Glissez-déposez des images ici, ou cliquez pour sélectionner
            </p>
            <p className="text-sm text-gray-500">
              PNG, JPG, WEBP jusqu'à 10MB ({maxFiles} fichiers max)
            </p>
          </div>
        )}
      </div>

      {uploads.length > 0 && (
        <div className="space-y-2">
          {uploads.map((upload) => (
            <div key={upload.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <ImageIcon className="h-5 w-5 text-gray-400" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {upload.file.name}
                </p>
                {upload.error ? (
                  <p className="text-sm text-red-600">{upload.error}</p>
                ) : upload.url ? (
                  <p className="text-sm text-green-600">Upload terminé</p>
                ) : (
                  <p className="text-sm text-gray-500">Upload en cours...</p>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeUpload(upload.id)}
                disabled={isUploading}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};